import type { ReactNode } from 'react';
import { Surface } from '../ui/Surface';

// ------------------------------------------------------------------
// ExampleFrame — story 2-147p (P10 §4, DOD). The individual demo box inside
// a GallerySection: one labeled example of a primitive/composite in a
// specific configuration. Formalizes the ad hoc bordered `<div>` wrappers
// (`rounded-hairline border border-border bg-card p-4`, `rounded-lg
// border border-dashed border-border...`) that recur, hand-written, dozens
// of times across `DesignSystemPage.tsx` today — one shared component
// instead of re-deriving the same box per call site.
//
// `dashed` switches to the "simulated/placeholder" border style the
// gallery already uses tree-wide for stand-in content (a 390px mobile-sim
// box, a report-content placeholder) — distinct from the default solid
// border, which frames a genuinely real, live primitive instance.
// ------------------------------------------------------------------

export interface ExampleFrameProps {
  /** Short caption above the demo (e.g. a variant name or state label). */
  label?: ReactNode;
  /** Optional explanatory note below the demo. */
  description?: ReactNode;
  /** Uses the dashed "placeholder/simulated" border instead of the default solid one. */
  dashed?: boolean;
  children: ReactNode;
}

// Story 2-147xd: dropped the `className` escape hatch (design-language/
// no-classname-escape-hatch — gallery is a finished-feature-unit layer per
// P10 §4.4's own table, not a slot-composition one; `className` there is
// reserved for primitive/layout-primitive/specialist-renderer). Its only
// two real call sites (`DesignSystemPage.tsx`'s Inline/Cluster examples,
// both `className="max-w-xs"`) now wrap the whole `<ExampleFrame>` in a
// plain `<div className="max-w-xs">` at the call site instead — same
// rendered width, no prop needed here.
export function ExampleFrame({ label, description, dashed = false, children }: ExampleFrameProps): JSX.Element {
  // Story 2-147ta: composes `Surface` (flat, `md` padding — the same p-4
  // this frame always rendered) instead of re-deriving border/bg/radius by
  // hand. `dashed` appends `border-dashed` on top of `Surface`'s own
  // `border-border` — a genuinely independent CSS property (border-style vs.
  // border-color), so no ordering conflict; byte-identical to the prior
  // `border-dashed border-border` combination.
  return (
    <Surface variant="flat" padding="md" className={dashed ? 'border-dashed' : ''}>
      {label && <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>}
      <div className={label ? 'mt-2' : undefined}>{children}</div>
      {description && <p className="mt-2 text-xs text-muted-foreground">{description}</p>}
    </Surface>
  );
}
